<script setup lang="ts">
// App-wide confirm dialog. Mounted once in AppShell; views call
// `useConfirmStore().ask({...})` and the next request renders here.
//
// Keyboard:
//   * Escape         → cancel
//   * Enter on confirm-focused → confirm
//   * Enter on cancel-focused  → cancel
//
// Autofocus:
//   * danger=true  → focus the Cancel button (safer default for
//                    destructive flows; an accidental Enter keypress
//                    while the modal opens won't trigger the destructive
//                    action).
//   * danger=false → focus the Confirm button.

import AppIcon from '@/components/AppIcon.vue';
import { computed, nextTick, ref, watch } from 'vue';
import Modal from './Modal.vue';

import { useConfirmStore } from '@/stores/confirm';
import { useI18n } from '@/lib/i18n';

const { t } = useI18n();
const store = useConfirmStore();
const open  = computed(() => store.pending !== null);
const req   = computed(() => store.pending);

const confirmBtn = ref<HTMLButtonElement | null>(null);
const cancelBtn  = ref<HTMLButtonElement | null>(null);
const typeInput  = ref<HTMLInputElement | null>(null);

const confirmLabel = computed(() =>
  req.value?.confirmLabel ?? (req.value?.danger ? t('common.delete') : t('common.confirm')),
);
const cancelLabel = computed(() => req.value?.cancelLabel ?? t('common.cancel'));

// Type-to-confirm: when the request carries `confirmText`, the user must
// type it verbatim before the confirm button enables.
const typed     = ref('');
const needsType = computed(() => !!req.value?.confirmText);
const matches   = computed(() =>
  !needsType.value || typed.value.trim() === req.value!.confirmText,
);

// Async-action state. When the request carries `onConfirm`, confirming
// runs it with the dialog held open + a spinner, surfacing errors inline.
const busy        = ref(false);
const actionError = ref<string | null>(null);

// Optional opt-in checkbox; its state is handed to `onConfirm`.
const checked = ref(false);

// Auto-focus the right element when a request opens; reset per-request
// transient state each open/close.
watch(open, async (isOpen) => {
  typed.value = '';
  busy.value = false;
  actionError.value = null;
  checked.value = req.value?.checkbox?.default ?? false;
  if (!isOpen) return;
  await nextTick();
  if (needsType.value) {
    typeInput.value?.focus();
  } else {
    const target = req.value?.danger ? cancelBtn.value : confirmBtn.value;
    target?.focus();
  }
});

async function onConfirm(): Promise<void> {
  if (!matches.value || busy.value) return;
  const action = req.value?.onConfirm;
  if (!action) {
    store.resolveAndClose(true);
    return;
  }
  busy.value = true;
  actionError.value = null;
  try {
    await action({ checked: checked.value });
    store.resolveAndClose(true);
  } catch (e) {
    actionError.value =
      (e as { error?: string })?.error
      ?? (e instanceof Error ? e.message : t('confirm.action_error'));
    busy.value = false;
  }
}
function onCancel(): void {
  if (busy.value) return;   // don't bail mid-action
  store.resolveAndClose(false);
}
</script>

<template>
  <Modal
    :open="open"
    :title="req?.title"
    :icon="req?.danger ? 'warning' : 'question'"
    :danger="req?.danger"
    :busy="busy"
    role="alertdialog"
    @close="onCancel"
  >
    <p v-if="req?.body" class="body">{{ req.body }}</p>

    <button
      v-if="req?.checkbox"
      type="button"
      class="checkbox"
      role="checkbox"
      :aria-checked="checked"
      :disabled="busy"
      @click="checked = !checked"
    >
      <span class="checkbox__box" :class="{ 'checkbox__box--on': checked }">
        <AppIcon name="check" v-if="checked" :size="13" />
      </span>
      <span class="checkbox__label">{{ req.checkbox.label }}</span>
    </button>

    <div v-if="req?.confirmText" class="confirm-type">
      <label class="confirm-type__label" for="confirm-type-input">
        {{ t('confirm.type_before') }} <code>{{ req.confirmText }}</code> {{ t('confirm.type_after') }}
      </label>
      <input
        id="confirm-type-input"
        ref="typeInput"
        v-model="typed"
        type="text"
        class="confirm-type__input"
        autocomplete="off"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        :disabled="busy"
        @keydown.enter.prevent="onConfirm"
      />
    </div>

    <p v-if="actionError" class="action-error">
      <AppIcon name="warning-circle" :size="14" />
      {{ actionError }}
    </p>

    <template #footer>
      <button
        ref="cancelBtn"
        type="button"
        class="btn btn-secondary"
        :disabled="busy"
        @click="onCancel"
      >{{ cancelLabel }}</button>
      <button
        ref="confirmBtn"
        type="button"
        :class="['btn', req?.danger ? 'btn-danger' : 'btn-primary']"
        :disabled="!matches || busy"
        @click="onConfirm"
      >
        <AppIcon name="circle-notch" v-if="busy" :size="15" class="spin" />
        {{ confirmLabel }}
      </button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
@layer surface {
  .body {
    margin: 0;
    color: var(--ink-muted);
    font-size: var(--text-sm);
    line-height: var(--leading-base);
    white-space: pre-wrap;     // preserve manual line breaks in body copy
  }

  .confirm-type { display: flex; flex-direction: column; gap: var(--space-2); }
  .confirm-type__label {
    font-size: var(--text-sm);
    color: var(--ink-muted);
    code {
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      color: var(--ink-primary);
      background: var(--surface-sunken);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-input);
      padding: 1px 6px;
    }
  }
  .confirm-type__input {
    width: 100%;
    padding: var(--space-2) var(--space-3);
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    color: var(--ink-primary);
    background: var(--surface-panel);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-input);
    &:focus { outline: 2px solid var(--accent-default); outline-offset: 1px; }
  }

  .checkbox {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    background: none;
    border: 0;
    padding: 0;
    text-align: left;
    cursor: pointer;
    color: var(--ink-secondary);
    font-size: var(--text-sm);
    line-height: var(--leading-base);
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
  .checkbox__box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    border: 1px solid var(--ink-muted);
    border-radius: var(--radius-input);
    background: var(--surface-panel);
    color: var(--accent-on);
    transition: background var(--dur-1) var(--ease-out), border-color var(--dur-1) var(--ease-out);
    &--on { background: var(--accent-default); border-color: var(--accent-default); }
  }
  .checkbox__label { min-width: 0; }

  .action-error {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: 0;
    font-size: var(--text-sm);
    color: var(--status-danger-text);
  }
  .spin { animation: confirm-spin 0.8s linear infinite; }
}

@keyframes confirm-spin { to { transform: rotate(360deg); } }

/* Buttons come from base.css's single .btn system (.btn-secondary /
   .btn-primary / .btn-danger) - no local overrides here. */
</style>
